import { useMemo, useCallback, useState, useRef } from 'react';
import Select, { createFilter } from 'react-select';
import { Menu, MenuItem } from '@mui/material';
import { colors, selectStyles } from '../../config/constants';
import { ValueContainer, DropdownIndicator, InputOptionActionable } from '../../config/customSelect';
import CustomMenuList from '../Helper/CustomMenuList';
import { FaRegTrashAlt, FaRegStar } from 'react-icons/fa';
import { BsPencilSquare } from 'react-icons/bs';
import FilterSetRenameModal from '../Filters/FilterSetRenameModal';
import FilterSetDeleteModal from '../Filters/FilterSetDeleteModal';

const SelectActionableOption = ({
  options = [],
  onChange,
  value,
  onApplySearch,
  inputValue: externalInputValue = '',
  onInputChange,
  onRenameConfirm,
  onDeleteConfirm,
  onSetDefaultConfirm,
  filterSetsData = [],
  styles: customStyles,
  maxMenuHeight: maxMenuHeightProp,
  menuPlacement: menuPlacementProp,
  menuPortalTarget: menuPortalTargetProp,
  onMenuOpen: onMenuOpenProp,
  onMenuClose: onMenuCloseProp,
  filterOption,
  ...props
}) => {
  const selectRef = useRef(null);
  const containerRef = useRef(null);
  const [filteredSearchResults, setFilteredSearchResults] = useState([]);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [activeActionOption, setActiveActionOption] = useState(null);
  const [isSelectMenuOpen, setIsSelectMenuOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dynamicMenuPlacement, setDynamicMenuPlacement] = useState('auto');
  const [dynamicMaxMenuHeight, setDynamicMaxMenuHeight] = useState(maxMenuHeightProp || 240);

  const handleChange = useCallback((newValue) => {
    if (!newValue) {
      onChange?.(newValue);
      return;
    }
    if (Array.isArray(newValue)) {
      const lastValue = newValue[newValue.length - 1];
      if (lastValue?.isSelectAll) {
        const allSelectableOptions = options.filter(
          opt => !opt.isSelectAll && !opt.isSelectAllMatching
        );
        onChange?.(allSelectableOptions);
        return;
      }
      if (lastValue?.isSelectAllMatching) {
        onChange?.(filteredSearchResults);
        return;
      }
      const filtered = newValue.filter(v => !v.isSelectAll && !v.isSelectAllMatching);
      onChange?.(filtered);
    } else {
      onChange?.(newValue);
    }
  }, [onChange, options, filteredSearchResults]);

  const handleInputChange = useCallback((inputVal, actionMeta) => {
    if (inputVal) {
      const filteredOpts = options.filter(opt => {
        const label = opt.label || '';
        return label.toLowerCase().includes(inputVal.toLowerCase());
      });
      setFilteredSearchResults(filteredOpts);
    } else {
      setFilteredSearchResults([]);
    }

    onInputChange?.(inputVal, actionMeta);
  }, [onInputChange, options]);

  const onApplyClick = useCallback(() => {
    onApplySearch?.();

    // Close menu
    selectRef.current?.blur();
  }, [onApplySearch]);

  const handleOptionActionClick = useCallback((anchorEl, option) => {
    const rect = anchorEl?.getBoundingClientRect?.();
    if (rect) {
      setActionMenuPosition({
        top: Math.round(rect.top),
        left: Math.round(rect.right),
      });
    }
    setActiveActionOption(option);
    setIsSelectMenuOpen(true);
  }, []);

  const handleActionMenuClose = useCallback((event) => {
    if (event) {
      event.stopPropagation();
    }
    setActionMenuPosition(null);
  }, []);

  const handleRenameClick = useCallback((event) => {
    if (event) {
      event.stopPropagation();
    }
    setActionMenuPosition(null);
    setIsRenameModalOpen(true);
    setIsSelectMenuOpen(false);
  }, []);

  const handleDeleteClick = useCallback((event) => {
    if (event) {
      event.stopPropagation();
    }
    setActionMenuPosition(null);
    setIsDeleteModalOpen(true);
    setIsSelectMenuOpen(false);
  }, []);

  const handleSetDefaultClick = useCallback((event) => {
    if (event) {
      event.stopPropagation();
    }
    setActionMenuPosition(null);
    onSetDefaultConfirm?.({id: activeActionOption?.value});
    setIsSelectMenuOpen(false);
  }, [activeActionOption, onSetDefaultConfirm]);

  const closeRenameModal = useCallback(() => {
    setIsRenameModalOpen(false);
    setActiveActionOption(null);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setActiveActionOption(null);
  }, []);

  const handleSelectMenuOpen = useCallback(() => {
    setIsSelectMenuOpen(true);
  }, []);

  const handleSelectMenuClose = useCallback(() => {
    if (actionMenuPosition) {
      return;
    }
    setIsSelectMenuOpen(false);
  }, [actionMenuPosition]);

  const selectComponents = useMemo(() => ({
    Option: (props) => <InputOptionActionable {...props} filterSetsData={filterSetsData} />,
    ValueContainer,
    DropdownIndicator,
    MenuList: CustomMenuList,
    ...props.components,
  }), [props.components, filterSetsData]);

  const updateMenuViewportConfig = useCallback(() => {
    if (!containerRef.current || typeof window === 'undefined') {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const viewportPadding = 12;
    const spaceBelow = viewportHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;

    if (!menuPlacementProp) {
      const nextPlacement = spaceBelow >= 200 || spaceBelow >= spaceAbove ? 'bottom' : 'top';
      setDynamicMenuPlacement(nextPlacement);
    }

    if (!maxMenuHeightProp) {
      const availableSpace = Math.max(spaceBelow, spaceAbove);
      const nextMaxMenuHeight = Math.max(120, Math.floor(Math.min(360, availableSpace)));
      setDynamicMaxMenuHeight(nextMaxMenuHeight);
    }
  }, [maxMenuHeightProp, menuPlacementProp]);

  const mergedStyles = useMemo(() => {
    const combinedStyles = {
      ...selectStyles,
      ...customStyles,
    };

    const baseMenuPortal = selectStyles.menuPortal;
    const customMenuPortal = customStyles?.menuPortal;

    combinedStyles.menuPortal = (provided, state) => {
      const base = baseMenuPortal ? baseMenuPortal(provided, state) : provided;
      const customized = customMenuPortal ? customMenuPortal(base, state) : base;
      return {
        ...customized,
        zIndex: Math.max(customized?.zIndex || 0, 1400),
      };
    };

    return combinedStyles;
  }, [customStyles]);

  const resolvedMenuPortalTarget = useMemo(() => {
    if (menuPortalTargetProp !== undefined) {
      return menuPortalTargetProp;
    }

    return typeof document !== 'undefined' ? document.body : null;
  }, [menuPortalTargetProp]);

  const handleMenuOpen = useCallback(() => {
    updateMenuViewportConfig();
    handleSelectMenuOpen();
    onMenuOpenProp?.();
  }, [handleSelectMenuOpen, onMenuOpenProp, updateMenuViewportConfig]);

  const handleMenuClose = useCallback(() => {
    handleSelectMenuClose();
    onMenuCloseProp?.();
  }, [handleSelectMenuClose, onMenuCloseProp]);

  return (
    <>
      <div ref={containerRef}>
        <Select
          {...props}
          ref={selectRef}
          captureMenuScroll={false}
          hideSelectedOptions={false}
          classNamePrefix="custom-select "
          options={options}
          maxMenuHeight={maxMenuHeightProp || dynamicMaxMenuHeight}
          onChange={handleChange}
          onInputChange={handleInputChange}
          value={value}
          menuIsOpen={isSelectMenuOpen}
          onMenuOpen={handleMenuOpen}
          onMenuClose={handleMenuClose}
          menuPlacement={menuPlacementProp || dynamicMenuPlacement}
          menuPosition='fixed'
          menuPortalTarget={resolvedMenuPortalTarget}
          menuShouldScrollIntoView={false}
          closeMenuOnSelect={false}
          onApplyClick={onApplyClick}
          onOptionActionClick={handleOptionActionClick}
          styles={mergedStyles}
          components={selectComponents}
          filterOption={filterOption || ((option, inputValue) => {
            if (option.data?.isSelectAll || option.data?.isSelectAllMatching) {
              return true;
            }
            return createFilter({ ignoreAccents: false })(option, inputValue);
          })}
        />
      </div>
      <Menu
        id="saved-filter-set-option-menu"
        anchorReference="anchorPosition"
        anchorPosition={actionMenuPosition || { top: 0, left: 0 }}
        open={Boolean(actionMenuPosition)}
        onClose={handleActionMenuClose}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              ml: 0.5,
              minWidth: 168,
            },
          },
        }}
      >
        {!filterSetsData.find(set => set.id === activeActionOption?.value)?.isDefault &&
          <MenuItem onClick={handleSetDefaultClick}><span>Set as Default</span><FaRegStar className='ms-auto' color={colors.yellow}/></MenuItem>
        }
        <MenuItem onClick={handleRenameClick}><span>Rename</span><BsPencilSquare className='ms-auto' color={colors.blueFilter}/></MenuItem>
        <MenuItem onClick={handleDeleteClick}><span>Delete</span><FaRegTrashAlt className='ms-auto' color='red'/></MenuItem>
      </Menu>
      <FilterSetRenameModal
        isOpen={isRenameModalOpen}
        option={activeActionOption}
        onConfirm={onRenameConfirm}
        onClose={closeRenameModal}
      />
      <FilterSetDeleteModal
        isOpen={isDeleteModalOpen}
        option={activeActionOption}
        onConfirm={onDeleteConfirm}
        onClose={closeDeleteModal}
      />
    </>
  );
};

export default SelectActionableOption;
